import { NavigationUserType } from './nav-items-misc';
import { createNavigationItem, type NavigationGooglePermissions, type NavigationGroupItem } from './side-nav-items';

export const SHARE_GROUP_FEATURES = [
  'curatedSocial',
  'socialMedia',
  'google',
  'widgets',
  'integrations',
  'marketReport',
  'promoter',
  'awards',
] as const;
export type ShareGroupFeature = (typeof SHARE_GROUP_FEATURES)[number];

export const getShareGroupItems = (
  features: ShareGroupFeature[],
  userType: NavigationUserType,
  hasMarketReports: boolean,
  googlePermissions: NavigationGooglePermissions,
  baseUrl: string,
): NavigationGroupItem[] => {
  return features.map((f) => {
    switch (f) {
      case 'awards':
        return createNavigationItem('awards', `/awards`, 'rma-award', [`/awards/`]);
      case 'curatedSocial':
        return createNavigationItem('curatedSocial', `/connect/studio`, 'rma-curated-social', [`/connect/studio/`]);
      case 'google':
        return createNavigationItem('googleProfile', getGoogleReviewsUrl(googlePermissions), 'social-google', [
          'google-profile/google-reviews/*',
        ]);
      case 'socialMedia':
        return createNavigationItem('socialMediaManager', `/socialmedia`, 'rma-smm', ['/socialmedia/*']);
      case 'promoter':
        return createNavigationItem('digitalAds', getPromoterUrl(baseUrl, userType), 'rma-promoter', [], '_self');
      case 'widgets':
        return createNavigationItem('widgets', '/widgets/library', 'public', ['/widgets/*']);
      case 'integrations':
        return createNavigationItem('integrations', '/integrations/thirdparty', 'apps');
      case 'marketReport':
        return createNavigationItem('marketReports', `/market-reports/${hasMarketReports ? 'manage' : 'about'}`, 'assignment', [
          `/${userType}/market-reports/*`,
        ]);
    }
  });
};

export function filterShareItems(
  showIntegrations: boolean,
  hideMarketReport: boolean,
  hasAwards: boolean,
  showCuratedSocial: boolean,
  showPromoter = true,
): (value: ShareGroupFeature, index: number, array: readonly ShareGroupFeature[]) => boolean {
  return (item) => {
    if (item === 'awards') {
      return hasAwards;
    }
    if (item === 'integrations') {
      return showIntegrations;
    }
    if (item === 'marketReport') {
      return !hideMarketReport;
    }
    if (item === 'curatedSocial') {
      return showCuratedSocial;
    }
    if (item === 'promoter') {
      return showPromoter;
    }
    return true;
  };
}

function getGoogleReviewsUrl(permissions: NavigationGooglePermissions): string {
  const { hasConnectedGmb, hasGoogleConnect, hasGoogleAskForReviews, hasOwnGoogleAskForReviews } = permissions;
  // Show Review and Setting if user has connected their GMB and has a subscription. Do not show if their agency controls the actual requesting
  if (hasConnectedGmb && hasGoogleConnect && hasGoogleAskForReviews && hasOwnGoogleAskForReviews) {
    return '/google-profile/google-reviews';
  }

  // Show about page and setting if user is connected to GMB but doesn't have subscription access.
  if (hasConnectedGmb && !hasOwnGoogleAskForReviews && !hasGoogleAskForReviews) {
    return '/google-profile/about';
  }

  // Show setting if user is connected to GMB but doesn't have it's own subscription access.
  if (hasConnectedGmb && !hasOwnGoogleAskForReviews && hasGoogleAskForReviews) {
    return '/google-profile/google-reviews/settings';
  }

  // Show get started page if user is not connected to GMB but have subscription access.
  if (!hasConnectedGmb && hasGoogleConnect) {
    return '/google-profile/connect/get-started';
  }

  // Show about page if user isn't connected to GMB or doesn't have access.
  if (!hasConnectedGmb && !hasGoogleConnect) {
    return '/google-profile/about';
  }

  return '/google-profile/about';
}

function getPromoterUrl(baseUrl: string, userType: NavigationUserType): string {
  switch (userType) {
    case 'agent':
    case 'agency':
      return `${baseUrl}/promoter/${userType}/promotions`;
    case 'team':
      return `${baseUrl}/promoter/agency/promotions`;
  }
}
